import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  GameController,
  Star,
  Check,
  Trophy,
  Timer,
  ArrowRight,
  Spinner,
  ArrowsClockwise,
  Lock,
  CheckCircle,
  Textbox,
  X,
} from '@phosphor-icons/react';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Word Match Game Component
const WordMatchGame = ({ game, onComplete }) => {
  const pairs = game.content?.pairs || [];
  const [selected, setSelected] = useState([]);
  const [matched, setMatched] = useState([]);
  const [wrongPair, setWrongPair] = useState(null);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameOver, setGameOver] = useState(false);

  const allItems = React.useMemo(() => {
    const left = pairs.map((p, i) => ({ id: `es-${i}`, text: p.es, type: 'es', idx: i, image_url: p.image_url }));
    const right = pairs.map((p, i) => ({ id: `de-${i}`, text: p.de, type: 'de', idx: i, image_url: p.image_url }));
    return [...left.sort(() => Math.random() - 0.5), ...right.sort(() => Math.random() - 0.5)];
  }, [pairs]);

  useEffect(() => {
    if (gameOver || matched.length === pairs.length * 2) return;
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timer); setGameOver(true); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameOver, matched.length, pairs.length]);

  useEffect(() => {
    if (matched.length === pairs.length * 2 && !gameOver) {
      setGameOver(true);
      onComplete(score + timeLeft);
    }
  }, [matched, pairs.length, gameOver, score, timeLeft, onComplete]);

  const handleSelect = (item) => {
    if (matched.includes(item.id) || gameOver) return;
    if (selected.length === 0) {
      setSelected([item]);
    } else if (selected.length === 1) {
      const first = selected[0];
      if (first.id === item.id) return;
      if (first.type !== item.type && first.idx === item.idx) {
        setMatched(prev => [...prev, first.id, item.id]);
        setScore(s => s + 10);
        setSelected([]);
      } else {
        setWrongPair([first.id, item.id]);
        setTimeout(() => { setWrongPair(null); setSelected([]); }, 600);
      }
    }
  };

  return (
    <div data-testid="word-match-game">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star size={20} weight="fill" className="text-amber-500" />
          <span className="font-bold text-[#292524]">{score} Punkte</span>
        </div>
        <div className="flex items-center gap-2">
          <Timer size={20} className={timeLeft < 10 ? 'text-red-500' : 'text-[#78716C]'} />
          <span className={`font-mono font-bold ${timeLeft < 10 ? 'text-red-500' : 'text-[#292524]'}`}>{timeLeft}s</span>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {allItems.map(item => {
          const isMatched = matched.includes(item.id);
          const isSelected = selected.some(s => s.id === item.id);
          const isWrong = wrongPair?.includes(item.id);
          return (
            <button
              key={item.id}
              onClick={() => handleSelect(item)}
              disabled={isMatched || gameOver}
              className={`p-3 rounded-xl text-sm font-medium transition-all min-h-[56px] flex flex-col items-center justify-center gap-1 ${
                isMatched ? 'bg-green-100 text-green-700 border-2 border-green-400' :
                isWrong ? 'bg-red-100 text-red-700 border-2 border-red-400 animate-shake' :
                isSelected ? 'bg-[#AB1C24]/20 text-[#AB1C24] border-2 border-[#AB1C24]' :
                'bg-white text-[#292524] border-2 border-[#D6D3D1] hover:border-[#AB1C24]/50'
              } ${item.type === 'es' ? 'font-semibold' : 'italic'}`}
              data-testid={`game-card-${item.id}`}
            >
              {item.image_url && (
                <img src={item.image_url} alt="" className="w-12 h-12 rounded-md object-cover" />
              )}
              <span className="flex items-center">
                {isMatched ? <Check size={16} className="inline mr-1" /> : null}
                {item.text}
              </span>
            </button>
          );
        })}
      </div>
      {gameOver && (
        <div className="mt-4 text-center bg-[#F5F5F4] rounded-xl p-4">
          <Trophy size={32} className="mx-auto mb-2 text-amber-500" weight="fill" />
          <p className="font-heading font-bold text-lg">{matched.length === pairs.length * 2 ? 'Perfekt!' : 'Zeit abgelaufen!'}</p>
          <p className="text-[#78716C]">{score + (matched.length === pairs.length * 2 ? timeLeft : 0)} Punkte</p>
        </div>
      )}
    </div>
  );
};

// Sentence Order Game
const SentenceOrderGame = ({ game, onComplete }) => {
  const sentences = game.content?.sentences || [];
  const [order, setOrder] = useState([]);
  const [available, setAvailable] = useState([]);
  const [checked, setChecked] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    setAvailable(sentences.map((s, i) => ({ ...s, idx: i })).sort(() => Math.random() - 0.5));
  }, [sentences]);

  const addToOrder = (item) => {
    setOrder(prev => [...prev, item]);
    setAvailable(prev => prev.filter(a => a.idx !== item.idx));
  };
  const removeFromOrder = (item) => {
    setAvailable(prev => [...prev, item]);
    setOrder(prev => prev.filter(o => o.idx !== item.idx));
  };
  const checkOrder = () => {
    let correct = 0;
    order.forEach((item, i) => { if (item.idx === i) correct++; });
    const s = Math.round((correct / sentences.length) * game.points_reward);
    setScore(s);
    setChecked(true);
    onComplete(s);
  };

  return (
    <div data-testid="sentence-order-game">
      <p className="text-[#57534E] mb-4">Bringe die Sätze in die richtige Reihenfolge:</p>
      <div className="space-y-2 mb-4 min-h-[100px] bg-[#F5F5F4] rounded-xl p-3">
        {order.length === 0 && <p className="text-[#78716C] text-center py-4">Klicke auf die Sätze unten</p>}
        {order.map((item, i) => (
          <button
            key={item.idx}
            onClick={() => !checked && removeFromOrder(item)}
            className={`w-full p-3 rounded-lg text-left text-sm ${
              checked ? (item.idx === i ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700') :
              'bg-white text-[#292524] hover:bg-gray-50'
            } border border-[#D6D3D1]`}
          >
            <span className="font-bold mr-2">{i + 1}.</span> {item.text}
          </button>
        ))}
      </div>
      {available.length > 0 && (
        <div className="space-y-2 mb-4">
          {available.map(item => (
            <button
              key={item.idx}
              onClick={() => addToOrder(item)}
              className="w-full p-3 rounded-lg text-left text-sm bg-white text-[#292524] border border-[#D6D3D1] hover:border-[#AB1C24]/50"
            >
              {item.text}
            </button>
          ))}
        </div>
      )}
      {!checked && order.length === sentences.length && (
        <Button onClick={checkOrder} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="check-order-btn">
          Überprüfen
        </Button>
      )}
      {checked && (
        <div className="text-center bg-[#F5F5F4] rounded-xl p-4">
          <p className="font-bold">{score} Punkte erhalten</p>
        </div>
      )}
    </div>
  );
};

// True/False Game
const TrueFalseGame = ({ game, onComplete }) => {
  const statements = game.content?.statements || [];
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [done, setDone] = useState(false);

  const answer = (choice) => {
    if (feedback !== null || done) return;
    const correct = statements[index].is_true === choice;
    setFeedback(correct ? 'correct' : 'wrong');
    setTimeout(() => {
      const newAnswers = [...answers, correct];
      setAnswers(newAnswers);
      setFeedback(null);
      if (index + 1 >= statements.length) {
        const correctCount = newAnswers.filter(Boolean).length;
        const score = Math.round((correctCount / statements.length) * game.points_reward);
        setDone(true);
        onComplete(score);
      } else {
        setIndex(index + 1);
      }
    }, 700);
  };

  if (done) {
    const correctCount = answers.filter(Boolean).length;
    return (
      <div className="text-center py-8" data-testid="true-false-done">
        <Trophy size={48} className="mx-auto mb-3 text-amber-500" weight="fill" />
        <p className="font-heading font-bold text-xl text-[#292524]">{correctCount} / {statements.length} richtig!</p>
      </div>
    );
  }

  const cur = statements[index];
  return (
    <div data-testid="true-false-game">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#78716C]">Aussage {index + 1} / {statements.length}</span>
        <div className="flex items-center gap-2">
          <Star size={18} weight="fill" className="text-amber-500" />
          <span className="font-bold text-[#292524]">{answers.filter(Boolean).length}</span>
        </div>
      </div>
      <div className={`rounded-2xl p-8 mb-6 text-center text-xl font-medium min-h-[140px] flex items-center justify-center transition-colors ${
        feedback === 'correct' ? 'bg-green-100 text-green-800' :
        feedback === 'wrong' ? 'bg-red-100 text-red-800' :
        'bg-[#F5F5F4] text-[#292524]'
      }`}>
        {cur?.text}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => answer(true)}
          disabled={feedback !== null}
          className="py-6 rounded-2xl bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="tf-true-btn"
        >
          ✅ Richtig
        </button>
        <button
          onClick={() => answer(false)}
          disabled={feedback !== null}
          className="py-6 rounded-2xl bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-bold text-lg shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="tf-false-btn"
        >
          ❌ Falsch
        </button>
      </div>
    </div>
  );
};

// Odd One Out Game
const OddOneOutGame = ({ game, onComplete }) => {
  const rounds = game.content?.rounds || [];
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);

  const handlePick = (wi) => {
    if (picked !== null || done) return;
    setPicked(wi);
    const isCorrect = wi === rounds[index].odd_index;
    const newCount = correctCount + (isCorrect ? 1 : 0);
    setCorrectCount(newCount);
    setTimeout(() => {
      setPicked(null);
      if (index + 1 >= rounds.length) {
        const score = Math.round((newCount / rounds.length) * game.points_reward);
        setDone(true);
        onComplete(score);
      } else {
        setIndex(index + 1);
      }
    }, 900);
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="odd-one-done">
        <Trophy size={48} className="mx-auto mb-3 text-amber-500" weight="fill" />
        <p className="font-heading font-bold text-xl text-[#292524]">{correctCount} / {rounds.length} richtig!</p>
      </div>
    );
  }

  const cur = rounds[index];
  return (
    <div data-testid="odd-one-game">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#78716C]">Runde {index + 1} / {rounds.length}</span>
        <div className="flex items-center gap-2">
          <Star size={18} weight="fill" className="text-amber-500" />
          <span className="font-bold text-[#292524]">{correctCount}</span>
        </div>
      </div>
      <div className="text-center mb-5">
        <p className="text-sm uppercase tracking-wider text-[#78716C] mb-1">Kategorie</p>
        <p className="font-heading text-2xl font-bold text-[#292524]">{cur?.category}</p>
        <p className="text-sm text-[#78716C] mt-1">Welches Wort passt nicht?</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cur?.words.map((w, wi) => {
          const isPicked = picked === wi;
          const isCorrect = picked !== null && wi === cur.odd_index;
          const isWrongPick = isPicked && wi !== cur.odd_index;
          const imgUrl = cur.images?.[wi];
          return (
            <button
              key={wi}
              onClick={() => handlePick(wi)}
              disabled={picked !== null}
              className={`py-5 rounded-2xl font-semibold text-lg shadow-sm border-2 transition-all hover:scale-[1.02] active:scale-[0.98] flex flex-col items-center justify-center gap-2 ${
                isWrongPick ? 'bg-red-100 border-red-400 text-red-800' :
                isCorrect ? 'bg-green-100 border-green-400 text-green-800' :
                'bg-white border-[#D6D3D1] text-[#292524] hover:border-[#AB1C24]/50'
              }`}
              data-testid={`odd-option-${wi}`}
            >
              {imgUrl && <img src={imgUrl} alt="" className="w-16 h-16 rounded-lg object-cover" />}
              {w}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Emoji Quiz
const EmojiQuizGame = ({ game, onComplete }) => {
  const questions = game.content?.questions || [];
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);

  const handlePick = (oi) => {
    if (picked !== null || done) return;
    setPicked(oi);
    const isCorrect = oi === questions[index].correct;
    const newCount = correctCount + (isCorrect ? 1 : 0);
    setCorrectCount(newCount);
    setTimeout(() => {
      setPicked(null);
      if (index + 1 >= questions.length) {
        const score = Math.round((newCount / questions.length) * game.points_reward);
        setDone(true);
        onComplete(score);
      } else {
        setIndex(index + 1);
      }
    }, 900);
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="emoji-quiz-done">
        <Trophy size={48} className="mx-auto mb-3 text-amber-500" weight="fill" />
        <p className="font-heading font-bold text-xl text-[#292524]">{correctCount} / {questions.length} richtig!</p>
      </div>
    );
  }

  const cur = questions[index];
  return (
    <div data-testid="emoji-quiz-game">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-[#78716C]">Frage {index + 1} / {questions.length}</span>
        <div className="flex items-center gap-2">
          <Star size={18} weight="fill" className="text-amber-500" />
          <span className="font-bold text-[#292524]">{correctCount}</span>
        </div>
      </div>
      <div className="rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 py-10 mb-6 text-center">
        {cur?.image_url ? (
          <img src={cur.image_url} alt="" className="mx-auto max-h-56 w-auto rounded-2xl shadow-md" />
        ) : (
          <div className="text-7xl sm:text-8xl leading-none select-none">{cur?.emoji}</div>
        )}
        <p className="text-sm text-[#78716C] mt-3">¿Cómo se dice en español?</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {cur?.options.map((opt, oi) => {
          const isPicked = picked === oi;
          const isCorrect = picked !== null && oi === cur.correct;
          const isWrongPick = isPicked && oi !== cur.correct;
          return (
            <button
              key={oi}
              onClick={() => handlePick(oi)}
              disabled={picked !== null}
              className={`py-5 px-3 rounded-2xl font-semibold text-base shadow-sm border-2 transition-all hover:scale-[1.02] active:scale-[0.98] ${
                isWrongPick ? 'bg-red-100 border-red-400 text-red-800' :
                isCorrect ? 'bg-green-100 border-green-400 text-green-800' :
                'bg-white border-[#D6D3D1] text-[#292524] hover:border-[#AB1C24]/50'
              }`}
              data-testid={`emoji-option-${oi}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Library Quiz (absorbed from ExercisesPage "Multiple Choice" and "Lückentext")
// Fetches library exercises of a given type and runs them one-by-one as a game.
const LibraryQuizGame = ({ exerciseType, level, onComplete }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        let url = `${API}/exercises?limit=10&exercise_type=${exerciseType}`;
        if (level && level !== 'all') url += `&level=${level}`;
        const res = await axios.get(url, { withCredentials: true });
        setItems((res.data || []).slice(0, 10));
      } catch { toast.error('Fehler beim Laden'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = items[idx];

  const submit = async () => {
    if (!current || selected == null) return;
    try {
      const res = await axios.post(`${API}/exercises/submit`, {
        exercise_id: current.exercise_id,
        answer: current.options[selected],
      }, { withCredentials: true });
      setResult(res.data);
      if (res.data.is_correct) setScore(s => s + 10);
    } catch { toast.error('Fehler'); }
  };

  const next = () => {
    const nextIdx = idx + 1;
    if (nextIdx >= items.length) {
      onComplete(score);
      return;
    }
    setIdx(nextIdx);
    setSelected(null);
    setResult(null);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>;
  }
  if (items.length === 0) {
    return <p className="text-center text-[#78716C] py-8">Keine Übungen verfügbar.</p>;
  }
  if (!current) return null;

  return (
    <div data-testid="library-quiz-game">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Star size={20} weight="fill" className="text-amber-500" />
          <span className="font-bold text-[#292524]">{score} Punkte</span>
        </div>
        <div className="text-sm text-[#78716C] font-medium">
          {idx + 1} / {items.length}
        </div>
      </div>

      <h3 className="font-heading font-semibold text-lg text-[#292524] mb-2">{current.title}</h3>
      <p className="text-[#57534E] mb-5">{current.question}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {current.options?.map((opt, i) => (
          <button
            key={i}
            onClick={() => !result && setSelected(i)}
            disabled={!!result}
            className={`p-4 rounded-xl text-left font-medium transition-all border-2 ${
              result
                ? opt === result.correct_answer
                  ? 'bg-green-50 border-green-400 text-green-800'
                  : i === selected
                    ? 'bg-red-50 border-red-400 text-red-800'
                    : 'bg-gray-50 border-gray-200 text-[#78716C]'
                : selected === i
                  ? 'bg-[#FDF0ED] border-[#AB1C24] text-[#AB1C24]'
                  : 'bg-white border-[#D6D3D1] text-[#292524] hover:border-[#AB1C24]/50'
            }`}
            data-testid={`library-quiz-option-${i}`}
          >
            {opt}
          </button>
        ))}
      </div>

      {!result ? (
        <Button
          onClick={submit}
          disabled={selected == null}
          className="w-full bg-[#AB1C24] hover:bg-[#8A151C]"
          data-testid="library-quiz-submit"
        >
          Antwort prüfen
        </Button>
      ) : (
        <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
          <div className={`flex items-center gap-2 ${result.is_correct ? 'text-green-700' : 'text-red-700'}`}>
            {result.is_correct ? <Check size={24} weight="bold" /> : <X size={24} weight="bold" />}
            <span className="font-semibold">{result.is_correct ? 'Richtig!' : 'Falsch'}</span>
            {result.is_correct && <span className="text-sm ml-2">+10</span>}
          </div>
          <Button onClick={next} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="library-quiz-next">
            {idx + 1 >= items.length ? 'Fertig' : 'Weiter'} <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

const GAME_TYPE_LABELS = {
  word_match: '🔗 Wort-Zuordnung',
  sentence_order: '📝 Satz-Reihenfolge',
  true_false: '✅ Richtig / Falsch',
  odd_one_out: '🎯 Eindringling finden',
  emoji_quiz: '😀 Emoji-Quiz',
  library_multiple_choice: '✅ Multiple Choice',
  library_fill_blank: '📝 Lückentext',
};

const GamesPage = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState('all');
  const [activeGame, setActiveGame] = useState(null);
  const [gameResult, setGameResult] = useState(null);

  // Synthetic "game" cards that wrap the MC/Lückentext library quizzes
  // so they live inside Mini-Juegos with the same card format.
  const SYNTHETIC_GAMES = [
    {
      game_id: 'library-multiple_choice',
      title: lang === 'es' ? 'Opción múltiple' : 'Multiple Choice',
      description: lang === 'es'
        ? 'Preguntas con opciones — 10 por ronda.'
        : 'Fragen mit mehreren Optionen — 10 pro Runde.',
      level: 'all',
      game_type: 'library_multiple_choice',
      _synthetic: true,
      _exerciseType: 'multiple_choice',
    },
    {
      game_id: 'library-fill_blank',
      title: lang === 'es' ? 'Rellenar huecos' : 'Lückentext',
      description: lang === 'es'
        ? 'Completa la palabra que falta — 10 por ronda.'
        : 'Fehlende Wörter einsetzen — 10 pro Runde.',
      level: 'all',
      game_type: 'library_fill_blank',
      _synthetic: true,
      _exerciseType: 'fill_blank',
    },
  ];

  useEffect(() => { loadGames(); }, [levelFilter]);

  const loadGames = async () => {
    try {
      let url = `${API}/games?limit=50`;
      if (levelFilter !== 'all') url += `&level=${levelFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setGames(res.data);
    } catch (e) { toast.error('Fehler'); }
    finally { setLoading(false); }
  };

  const handleGameComplete = async (score) => {
    if (!activeGame) return;
    if (activeGame._synthetic) {
      // Library quizzes don't map to a DB game — points already accrue via
      // /api/exercises/submit, so just show the summary screen.
      setGameResult({ points_earned: score });
      return;
    }
    try {
      const res = await axios.post(`${API}/games/${activeGame.game_id}/complete`, { score }, { withCredentials: true });
      setGameResult(res.data);
    } catch (e) { console.error(e); }
  };

  if (activeGame) {
    return (
      <div className="space-y-6" data-testid="game-active">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-[#292524]">{activeGame.title}</h2>
            <p className="text-sm text-[#78716C]">{activeGame.description}</p>
          </div>
          <Button variant="outline" onClick={() => { setActiveGame(null); setGameResult(null); }} data-testid="back-to-games-btn">
            Zurück
          </Button>
        </div>
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
          {activeGame.game_type === 'word_match' && <WordMatchGame game={activeGame} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'sentence_order' && <SentenceOrderGame game={activeGame} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'true_false' && <TrueFalseGame game={activeGame} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'odd_one_out' && <OddOneOutGame game={activeGame} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'emoji_quiz' && <EmojiQuizGame game={activeGame} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'library_multiple_choice' && <LibraryQuizGame exerciseType="multiple_choice" level={levelFilter} onComplete={handleGameComplete} />}
          {activeGame.game_type === 'library_fill_blank' && <LibraryQuizGame exerciseType="fill_blank" level={levelFilter} onComplete={handleGameComplete} />}
          {!['word_match', 'sentence_order', 'true_false', 'odd_one_out', 'emoji_quiz', 'library_multiple_choice', 'library_fill_blank'].includes(activeGame.game_type) && (
            <p className="text-[#78716C] text-center py-8">Spieltyp nicht unterstützt</p>
          )}
        </div>
        {gameResult && (
          <div className="bg-green-50 rounded-2xl border border-green-200 p-6 text-center">
            <Trophy size={40} className="mx-auto mb-3 text-amber-500" weight="fill" />
            <p className="font-heading font-bold text-xl text-[#292524]">+{gameResult.points_earned} Punkte!</p>
            <Button onClick={() => { setActiveGame(null); setGameResult(null); loadGames(); }} className="mt-4 bg-[#AB1C24] hover:bg-[#8A151C]">
              <ArrowRight size={18} className="mr-2" /> Weiter
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="games-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">Spiele</h1>
          <p className="text-[#57534E] mt-1">Lerne spielend Spanisch mit interaktiven Spielen</p>
        </div>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-[150px]" data-testid="games-level-filter">
            <SelectValue placeholder="Niveau" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Niveaus</SelectItem>
            <SelectItem value="A1">A1</SelectItem>
            <SelectItem value="A2">A2</SelectItem>
            <SelectItem value="B1">B1</SelectItem>
            <SelectItem value="B2">B2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...SYNTHETIC_GAMES, ...games].map(game => (
            <div key={game.game_id} className="bg-white rounded-2xl border border-[#D6D3D1] overflow-hidden hover:shadow-md transition-shadow" data-testid={`game-card-${game.game_id}`}>
              <div className="h-28 bg-gradient-to-br from-[#AB1C24]/20 to-[#8A151C]/20 flex items-center justify-center">
                <GameController size={48} className="text-[#AB1C24]" weight="duotone" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  {game.level && game.level !== 'all'
                    ? <span className={`level-badge ${game.level}`}>{game.level}</span>
                    : <span className="text-xs text-[#78716C] bg-[#F5F5F4] px-2 py-1 rounded-full">{lang === 'es' ? 'Todos' : 'Alle'}</span>}
                  <span className="text-xs text-[#78716C] bg-[#F5F5F4] px-2 py-1 rounded-full">{GAME_TYPE_LABELS[game.game_type] || game.game_type?.replace('_', ' ')}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#292524] mb-1">{game.title}</h3>
                <p className="text-sm text-[#78716C] mb-4 line-clamp-2">{game.description}</p>
                <Button onClick={() => setActiveGame(game)} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid={`play-game-${game.game_id}`}>
                  <GameController size={18} className="mr-2" /> {lang === 'es' ? 'Jugar' : 'Spielen'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GamesPage;
